import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { highlightTerms } from '../highlightTerms';
import { Zap } from 'lucide-react';

export const VideoSlide = ({ slide }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    // Ensure video plays when slide is shown
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [slide]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div
      className="h-full w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center px-8 md:px-12 lg:px-20 py-12"
      data-testid={`slide-${slide.id}`}
    >
      {/* Text Content */}
      <motion.div
        className="space-y-8 order-2 lg:order-1"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2
          variants={itemVariants}
          className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-none"
        >
          {highlightTerms(slide.title)}
        </motion.h2>

        <motion.div className="space-y-5">
          {slide.bullets?.map((bullet, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              className="flex items-start gap-4 group"
            >
              <div className="mt-1 p-2 rounded-lg bg-[#D31113]/20 group-hover:bg-[#D31113]/40 transition-colors">
                <Zap className="w-5 h-5 text-[#D31113]" />
              </div>
              <p className="font-sans text-lg md:text-xl text-gray-200 leading-relaxed">
                {highlightTerms(bullet)}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {slide.keyLine && (
          <motion.div
            variants={itemVariants}
            className="pt-6 border-t border-white/10"
          >
            <p className="font-sans text-xl md:text-2xl text-white font-semibold italic">
              "{highlightTerms(slide.keyLine)}"
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Video */}
      <motion.div
        className="relative order-1 lg:order-2 h-64 lg:h-full min-h-[300px] rounded-2xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={slide.video}
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#17072F]/40" />
        <div className="absolute inset-0 border-2 border-[#D31113]/30 rounded-2xl pointer-events-none" />
      </motion.div>
    </div>
  );
};
